import { RecommendedMpspPriorityTypes } from '~/gql/graphql'
import { TagType } from '../types'

export const TABS = ['lineItems', 'messages', 'history']

export const PLACEMENT = 'job-details-page'

export const QUEUE_STATUS = {
    PROGRESS: 'in_progress',
    EMPTY: 'empty',
    PAUSED: 'paused',
    ACCEPTED: 'accepted',
    ENDED: 'ended',
    CANCELLED: 'canceled',
} as const

export const PRIORITY_FILTER_MAPPING = {
    URGENT: RecommendedMpspPriorityTypes.URGENT,
    NORMAL: RecommendedMpspPriorityTypes.NORMAL,
    'VERY URGENT': RecommendedMpspPriorityTypes.VERY_URGENT,
} as const

export const QueueStatusTagMap: TagType = {
    [QUEUE_STATUS.PROGRESS]: { color: 'blue', title: 'In progress' },
    [QUEUE_STATUS.EMPTY]: { color: 'red', title: 'Empty' },
    [QUEUE_STATUS.PAUSED]: { color: 'red', title: 'Paused' },
    [QUEUE_STATUS.ACCEPTED]: { color: 'green', title: 'Accepted' },
    [QUEUE_STATUS.ENDED]: { color: 'red', title: 'Ended' },
    [QUEUE_STATUS.CANCELLED]: { color: '', title: 'Cancelled' },
}

export const QUEUE_ITEM_STATUS = {
    QUEUED: 'queued',
    SENT: 'sent',
    ACCEPTED: 'accepted',
    EXPIRED: 'expired',
    REJECTED: 'rejected',
    VIEWED: 'viewed',
    SKIPPED: 'skipped',
    CANCELLED: 'canceled',
    COMPLETED: 'completed',
} as const

export const QueueItemStatusTagMap: TagType = {
    [QUEUE_ITEM_STATUS.QUEUED]: { color: '', title: 'Queued' },
    [QUEUE_ITEM_STATUS.SENT]: { color: 'blue', title: 'Sent' },
    [QUEUE_ITEM_STATUS.ACCEPTED]: { color: 'green', title: 'Accepted' },
    [QUEUE_ITEM_STATUS.EXPIRED]: { color: '', title: 'Expired' },
    [QUEUE_ITEM_STATUS.REJECTED]: { color: 'red', title: 'Rejected' },
    [QUEUE_ITEM_STATUS.VIEWED]: { color: 'blue', title: 'Viewed' },
    [QUEUE_ITEM_STATUS.SKIPPED]: { color: 'orange', title: 'Skipped' },
    [QUEUE_ITEM_STATUS.CANCELLED]: { color: '', title: 'Cancelled' },
    [QUEUE_ITEM_STATUS.COMPLETED]: { color: '', title: 'Completed' },
}

export const JOB_STATUS = {
    UNACCEPTED: 'unaccepted',
    CANCELED: 'canceled',
    COMPLETED: 'completed',
    ACCEPTED: 'accepted',
    NO_JOB: 'no_job',
} as const

export const JobStatusTagMap: TagType = {
    [JOB_STATUS.UNACCEPTED]: { color: 'red', title: 'Unaccepted' },
    [JOB_STATUS.CANCELED]: { color: '', title: 'Cancelled' },
    [JOB_STATUS.COMPLETED]: { color: '', title: 'Completed' },
    [JOB_STATUS.ACCEPTED]: { color: 'green', title: 'Accepted' },
    [JOB_STATUS.NO_JOB]: { color: '', title: 'No job' },
}

export enum SchedulingQueueStatus {
    empty,
    paused,
    in_progress,
    accepted,
    ended,
    canceled,
}

export enum SchedulingMethod {
    auto = 'auto',
    rfq = 'rfq',
    select = 'select',
}

export const SchedulingMethodMap = {
    [SchedulingMethod.auto]: 'Auto',
    [SchedulingMethod.rfq]: 'RFQ',
    [SchedulingMethod.select]: 'Select',
}

export const SELECTION_CONTEXT = {
    JOB: 'JOB',
    QUEUE_ITEM: 'QUEUE-ITEM',
    JOB_REQUEST: 'JOB-REQUEST',
} as const

export const DEFAULT_ACCEPTANCE_MIN = 45
export const MINIMUM_ACCEPTANCE_MIN = 1
export const MINIMUM_EVALUATION_MIN = 0
export const DEFAULT_EVALUATION_MIN = 45
export const DEFAULT_WAIT_MIN = 5
export const MINIMUM_WAIT_MIN = 0

export enum StartTimeType {
    IMMEDIATE,
    NO_EARLIER_THAN,
}

export const JOB_REQUEST_STATUS = Object.freeze({
    CANCELED: 'canceled',
    ACCEPTED: 'accepted',
    REJECTED: 'rejected',
    EXPIRED: 'expired',
    VIEWED: 'viewed',
    SENT: 'sent',
})

export const ACTION_TYPES = {
    ACCEPT: 'accept',
    REJECT: 'reject',
    CANCEL: 'cancel',
    CANCEL_JOB: 'cancel-job',
    DOWNLOAD: 'download',
    PUSH_TO_JOB_BOARD: 'push-to-job-board',
}

export const REJECTION_REASONS = [
    {
        label: 'Low payout',
        value: 'low_payout',
        description:
            'The payout is insufficient relative to normal (non-overtime) production costs.',
    },
    {
        label: 'No capacity',
        value: 'no_capacity',
        description:
            'Current production schedule does not have capacity to accommodate this job.',
    },
    {
        label: 'Material sourcing',
        value: 'material_sourcing',
        description:
            'Current production schedule does not have capacity to accommodate this job.',
    },
    {
        label: 'Capabilities',
        value: 'capabilities',
        description:
            'MP does not currently have the capabilities required to complete this job.',
    },
    {
        label: 'Other',
        value: 'other',
        description:
            'The options above do not reflect reason for rejecting this job.',
    },
]

export const MATERIAL_SOURCING_OPTIONS = [
    'MP cannot source this material at all.',
    'The time needed to source this material would not allow MP to complete the job on time.',
]

export const REJECTION_TITLE =
    'Are you sure you want to reject this job request?'
export const REJECTION_MODAL_TEXT_PLACEHOLDER =
    "Please provide any further information you'd like to share with us."

const CHANGE_HISTORY_OBJECT_TYPES = { JOB: 'job', JOB_REQUEST: 'job_request' }

export const ChangeHistoryObjectTypeMap = {
    [CHANGE_HISTORY_OBJECT_TYPES.JOB]: { title: 'Job' },
    [CHANGE_HISTORY_OBJECT_TYPES.JOB_REQUEST]: { title: 'Job request' },
}

export const LOW_PAYOUT_OPTIONS = [
    { key: 'FIVE_TEN', value: '5%-10% higher' },
    { key: 'ELEVEN_TWENTY', value: '11%-20% higher' },
    { key: 'TWENTYONE_THIRTY', value: '21%-30% higher' },
    { key: 'THIRTYONE_FOURTY', value: '31%-40% higher' },
    { key: 'MORE_THAN_FOURTY', value: 'More than 40% higher' },
]

const REASON_TO_SKIP_JOB_REQUEST = {
    NON_JOB_ACCEPTING_HOURS: 'mp_non_job_accepting_hours',
    UNAVAILABLE: 'mp_unavailable',
    NOT_ELIGIBLE_TO_WORK: 'mp_not_eligible_to_work',
}

export const ReasonToSkipJobRequestMap = {
    [REASON_TO_SKIP_JOB_REQUEST.NON_JOB_ACCEPTING_HOURS]: {
        tooltip: 'Request sent outside of acceptance hours',
    },
    [REASON_TO_SKIP_JOB_REQUEST.UNAVAILABLE]: {
        tooltip: 'MP not accepting jobs',
    },
    [REASON_TO_SKIP_JOB_REQUEST.NOT_ELIGIBLE_TO_WORK]: {
        tooltip: 'MP not eligible to work',
    },
}

export const ACTION_TYPES_TITLE = {
    [ACTION_TYPES.ACCEPT]: 'Accept',
    [ACTION_TYPES.REJECT]: 'Reject',
    [ACTION_TYPES.CANCEL]: 'Cancel job request',
    [ACTION_TYPES.CANCEL_JOB]: 'Cancel job',
    [ACTION_TYPES.DOWNLOAD]: 'Download job bundle',
    [ACTION_TYPES.DOWNLOAD]: 'Download job bundle',
    [ACTION_TYPES.PUSH_TO_JOB_BOARD]: 'Add to live job board',
}

export const EDIT_JOB_PAYOUT = 'Adjust job payout'

export const qcTypeData = [
    { label: 'Virtual', value: 'virtual' },
    { label: 'Physical', value: 'physical' },
]
